/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { ThemeKey } from "@smartsheet/design-tokens";
import { warnOnce } from "@smartsheet/lodestar-utils";
import { getClientId } from "@smartsheet/lodestar-utils";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { useActive } from "@smartsheet/lodestar-utils";
import { useSelect } from "downshift";
import { ButtonHTMLAttributes, KeyboardEvent, useRef, useState, useEffect, useCallback } from "react";
import { useTheme } from "../../../../theme/utils/useTheme";
import { InputFieldBaseProps } from "../../../../util/inputFieldBaseProps";
import { useHovered } from "../../../../util/useHovered";
import { useFocusMode } from "../../../focusMode";
import { FORM_LABEL_CLICK_EVENT_ID } from "../../../formlabel/FormLabel";
import { SELECT_TOGGLE_ELEMENT_CLASS } from "../../constants";
import { useA11yStatusMessages } from "../../hooks/useA11yStatusMessages";
import { useBlurOnChipBlur } from "../../hooks/useBlurChipOnBlur";
import { useHorizontalResize } from "../../hooks/useHorizontalResize";
import { useMultipleSelectionCustom } from "../../hooks/useMultipleSelectionCustom";
import { useMultiSelectOptionToString } from "../../hooks/useOptionToString";
import { useResetSelection } from "../../hooks/useResetSelection";
import { useSelectFlyout } from "../../hooks/useSelectFlyout";
import { BaseOptionType, FormatOption } from "../../types";
import {
    findNextValidOptionIndex,
    isSelectAllMarkerOption,
    isSelectV2OptionSeparator,
    stopPropagationHandler,
    useSelectIds,
} from "../../utils";
import { Actions } from "../shared/Actions";
import { HiddenInput } from "../shared/HiddenInput";
import { Root } from "../shared/Root";
import { SelectClearButton } from "../shared/SelectClearButton";
import { SelectMenu } from "../shared/SelectMenu";
import { SelectPlaceholder } from "../shared/SelectPlaceholder";
import { ToggleButton } from "../shared/ToggleButton";
import { MultiSelectOption } from "./MultiSelectOption";
import { MultiSelectValueContainer } from "./MultiSelectValueContainer";
import { OptionalPropsNowRequired } from "./types";

export interface MultiSelectBasicProps<Option extends BaseOptionType>
    extends InputFieldBaseProps,
        Omit<OptionalPropsNowRequired<Option, HTMLButtonElement>, "isSearchable" | "onCreateOption"> {
    /**
     * This set of options includes the select all.
     */
    memoizedOptions: Option[];

    /**
     * The logging hook is taken care of in the parent component.
     */
    onChange: (newOptions: Option[]) => void;
}

export const MultiSelectBasic = <Option extends BaseOptionType>({
    appearance = "outlined",
    "aria-describedby": ariaDescribedby,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-required": ariaRequired,
    classes,
    className,
    clientId,
    menuWidth = "match-control",
    enableSelectAll,
    formatOption,
    getHiddenInputValue,
    hiddenInputName,
    id,
    innerRef,
    isClearable = false,
    isLoading,
    isOptionDisabled,
    isReadonly,
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    maxSelectedRows,
    menuId,
    rollupSelectionAfter,
    memoizedOptions,
    menuPlacement = "bottom",
    menuPosition,
    onBlur,
    onChange,
    onFocus,
    optionHeight,
    options: providedOptions,
    optionToString,
    portalProps,
    selectedOptions,
    validationState,
    getOptionKey,
    menuIsOpen,
    setMenuIsOpen,
    onMenuIsOpenChanged,
    hideDropdownArrow = false,
    formatSelectedValue,
    placeholder,
    onKeyDown: providedOnKeyDown,
    inputFieldHeight,
    overrides,
}: MultiSelectBasicProps<Option>) => {
    const a11yMessages = useA11yStatusMessages();
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const { focusMode } = useFocusMode();

    // The flyout needs to point to the component container, but otherwise flyout interactions
    // should affect the input.
    const { ref: resizeRef, width: rootWidth } = useHorizontalResize<HTMLDivElement>({});
    const localContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useForkRef(localContainerRef, resizeRef);
    const localToggleRef = useRef<HTMLButtonElement>(null);
    const clearButtonRef = useRef<HTMLButtonElement>(null);
    const localOpenState = useRef<boolean>(false);
    const changeType = useRef<string | null>(null);
    const toggleRef = useForkRef(
        localToggleRef,
        // downshiftInputProps.ref as Ref<HTMLButtonElement>,
        innerRef || null
    );

    const [isFocused, setIsFocused] = useState(false);

    const isInteractive = !isReadonly;

    const multiSelectOptionToString = useMultiSelectOptionToString(optionToString, "");

    const {
        isHovered: isToggleHovered,
        onMouseLeave: onToggleMouseLeave,
        onMouseEnter: onToggleMouseEnter,
    } = useHovered<HTMLButtonElement>();

    const {
        isActive: isToggleActive,
        onMouseDown: onTAMouseDown,
        onMouseEnter: onTAMouseEnter,
        onMouseLeave: onTAMouseLeave,
    } = useActive({
        onMouseEnter: onToggleMouseEnter,
        onMouseLeave: onToggleMouseLeave,
    });

    const {
        addSelectedItem,
        getDropdownProps,
        getSelectedItemProps,
        isItemSelected,
        removeSelectedItem,
        selectedItems,
        setSelectedItems,
        setActiveIndex,
        activeIndex,
    } = useMultipleSelectionCustom({
        isInteractive,
        itemToString: multiSelectOptionToString,
        rollupSelectionAfter,
        onChange,
        selectedItems: selectedOptions,
    });

    // We must ignore the added internal options like "create X" and select all here.
    const allSelected = providedOptions.length === selectedItems.length;
    // Incorporate some so that select all functionality only shows when 0 items are selected
    const allOrSomeSelected = providedOptions.length === selectedItems.length || selectedItems.length > 0;
    const { resetSelection } = useResetSelection();
    const selectIds = useSelectIds("select", id);

    const {
        closeMenu,
        getItemProps,
        getMenuProps,
        getToggleButtonProps,
        highlightedIndex,
        setHighlightedIndex,
        isOpen,
        toggleMenu,
        openMenu,
        selectItem,
    } = useSelect({
        ...a11yMessages,
        ...selectIds,
        ...(menuIsOpen !== undefined ? { isOpen: menuIsOpen } : null),
        items: memoizedOptions,
        circularNavigation: false,
        itemToString: multiSelectOptionToString,
        stateReducer: (state, actionAndChanges) => {
            const { changes, type } = actionAndChanges;
            changeType.current = type;
            switch (type) {
                case useSelect.stateChangeTypes.MenuKeyDownEscape:
                    // Cancel the escape key handling. We want to handle this key on our own
                    // through the StackOrderManager.
                    return state;
                case useSelect.stateChangeTypes.MenuKeyDownEnter:
                case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                case useSelect.stateChangeTypes.ItemClick:
                    if (changes.selectedItem && isOptionDisabled?.(changes.selectedItem)) {
                        return state;
                    }
                    return {
                        ...changes,
                        // Retain the menu's state after a selection.
                        highlightedIndex: state.highlightedIndex,
                        isOpen: true,
                    };
            }

            if (changes.highlightedIndex) {
                switch (type) {
                    case useSelect.stateChangeTypes.MenuKeyDownArrowUp:
                    case useSelect.stateChangeTypes.MenuKeyDownArrowDown:
                        if (isSelectV2OptionSeparator(memoizedOptions[changes.highlightedIndex])) {
                            return {
                                ...changes,
                                highlightedIndex: findNextValidOptionIndex(
                                    memoizedOptions,
                                    changes.highlightedIndex,
                                    type === useSelect.stateChangeTypes.MenuKeyDownArrowDown
                                ),
                            };
                        }
                }
            }

            return changes;
        },
        onIsOpenChange: ({ isOpen: openState }) => {
            if (process.env.NODE_ENV !== "production") {
                warnOnceForDeprecatedSetMenuIsOpenProp =
                    warnOnceForDeprecatedSetMenuIsOpenProp ??
                    warnOnce(
                        "MultiSelectV2 (basic): setMenuIsOpen is deprecated. Please use onMenuIsOpenChanged instead."
                    );
                if (setMenuIsOpen) {
                    warnOnceForDeprecatedSetMenuIsOpenProp();
                }
            }
            // setMenuIsOpen is deprecated. Use onMenuIsOpenChanged if it's available instead.
            (onMenuIsOpenChanged ?? setMenuIsOpen)?.(!!openState);
        },
        onStateChange: ({ type, selectedItem }) => {
            switch (type) {
                case useSelect.stateChangeTypes.MenuKeyDownEnter:
                case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                case useSelect.stateChangeTypes.ItemClick:
                    if (selectedItem) {
                        if (enableSelectAll && isSelectAllMarkerOption(selectedItem)) {
                            if (!allOrSomeSelected) {
                                setSelectedItems(providedOptions);
                            } else {
                                setSelectedItems([]);
                            }
                            resetSelection(selectItem, 10);
                        } else {
                            if (isItemSelected(selectedItem)) {
                                removeSelectedItem(selectedItem);
                                // use a time < 200 ms because the default message that useSelect
                                // reads will debounce anything under 200ms and not call it's
                                // default handler. This means something will be deselected and the
                                // useMultipleSelection a11y status message will be the only one
                                // read out since from useSelect's perspective, the selectedItem
                                // changed from null to null. No change = an empty string for the
                                // a11y message.
                                resetSelection(selectItem, 10);
                            } else {
                                addSelectedItem(selectedItem);
                                // Use a timeout greater than 200ms. This means we'll allow
                                // useSelect to run its default a11y message updater and then
                                // reset the selected item to null.
                                // If we don't do this, there will be no message read on selection.
                                resetSelection(selectItem, 220);
                            }
                        }
                    }
                    break;
            }
        },
    });

    // Tracks the current height of the content box containing the Chips.
    // Update this to a new value to reposition the Flyout.
    // 20/24 are the default chip sizes.  Default to them to prevent a re-render for default sized comboboxes.
    const [chipContentHeight, setChipContentHeight] = useState(inputFieldHeight === 40 ? 24 : 20);

    const { flyoutProps, targetRef } = useSelectFlyout({
        menuId,
        clientId,
        closeMenu,
        isOpen,
        targetRef: containerRef,
        menuPlacement,
        menuPosition,
        portalProps,
        // Update positioning whenever the open state changes, or when the combobox height changes.
        // We need the open state because if it's closed, and something else moves the combobox, the
        // menu needs to recalc or it will still be pointing at the position of its initial render.
        updateKey: `${String(isOpen)}_${chipContentHeight}`,
    });

    const optionRenderer: FormatOption<Option> = (
        option,
        index,
        selectedIndex,
        optionSpreadProps,
        isSelected,
        metadata
    ) => {
        const optionLabel = optionToString(option);

        return (
            <MultiSelectOption
                height={optionHeight}
                checkedState={isItemSelected(option) ? "checked" : "unchecked"}
                aria-disabled={metadata.isDisabled}
                {...optionSpreadProps}
            >
                {optionLabel}
            </MultiSelectOption>
        );
    };
    const toggleButtonProps = getToggleButtonProps({
        // This will remove some mouse and key handling.
        // Unlike useCombobox, useSelect doesn't disable
        // disabled: !isInteractive,
        ...(getDropdownProps({
            // Don't let the container handle this.
            onClick: stopPropagationHandler,
            onFocus: () => {
                setActiveIndex(-1);
            },
            onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => {
                if (providedOnKeyDown) {
                    providedOnKeyDown(e, {
                        highlightedIndex,
                        options: providedOptions,
                    });
                }
            },
            "aria-describedby": ariaDescribedby,
            "aria-label": ariaLabel,
            "aria-labelledby": ariaLabelledby,
            "aria-required": ariaRequired,
            id,
            ref: toggleRef,
        }) as ButtonHTMLAttributes<HTMLButtonElement>),
    }) as ButtonHTMLAttributes<HTMLButtonElement>;

    if (!isInteractive) {
        delete toggleButtonProps["onClick"];
    }

    const showClearAction = isClearable && !isReadonly && selectedItems.length > 0;

    const handleChipNavigation = useCallback(
        (direction: "tab" | "down" | "up") => {
            switch (direction) {
                case "tab":
                    localToggleRef.current?.focus();
                    break;
                case "down":
                    openMenu();
                    setActiveIndex(-1);
                    setHighlightedIndex(0);
                    break;
                case "up":
                    openMenu();
                    setActiveIndex(-1);
                    setHighlightedIndex(memoizedOptions.length - 1);
                    break;
            }
        },
        [memoizedOptions.length, openMenu, setActiveIndex, setHighlightedIndex]
    );

    useBlurOnChipBlur({
        ref: localContainerRef,
        activeIndex: activeIndex,
        setActiveIndex: setActiveIndex,
    });

    const handleMultiSelectBasicFocus = (event: React.FocusEvent<HTMLDivElement>, type: "blur" | "focus") => {
        //only fire the passed in focus events when the target is the container
        //this way we are not firing the onFocus/onBlur events when the user is interacting with the flyout/button
        if (typeof event?.target?.getAttribute === "function" && event.target.getAttribute("role") === "listbox") {
            if (type === "focus") {
                setIsFocused(true);
                onFocus?.(event as unknown as React.FocusEvent<HTMLButtonElement>);
            } else {
                setIsFocused(false);
                onBlur?.(event as unknown as React.FocusEvent<HTMLButtonElement>);
            }
        } else {
            //manage focus for styles only without firing off onBlur or onFocus events
            if (type === "focus") {
                setIsFocused(true);
            } else {
                setIsFocused(false);
            }
        }
    };

    // This is needed because we want to close the menu when a user clicks for a second time inside the container/faux input
    // This behavior is consistent with our other select components.
    // The issue is that downshift's isOpen is false when the click event is fired, so we can't use that to determine if the select is open
    // previous order of events:
    // click container -> opens select -> focus -> click the container again while select is open -> blur -> onClick -> focus
    // since mouseDown is fired before the onClick and blur events, we can use it to know if the container was clicked after the select was opened
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (
            isOpen &&
            e.target instanceof HTMLElement &&
            typeof e.target.getAttribute === "function" &&
            e.target.getAttribute("data-client-id")?.includes("-value-container")
        ) {
            // we need to know the mouse down was on the container and not any of the children (toggle button, clear button, etc.)
            localOpenState.current = true;
        }
        if (!isOpen) {
            //resets the localOpenState if the select is closed
            localOpenState.current = false;
        }
    };

    useEffect(() => {
        if (!isOpen) {
            //wait a tick before resetting the changeType
            //we do this so label clicks can distinguish between
            //input blurs that happen when it's clicked vs
            //blurs from other element clicks.
            setTimeout(() => (changeType.current = null), 0);
        }
    }, [isOpen]);

    return (
        <Root
            inputFieldHeight={inputFieldHeight}
            data-client-id={getClientId(clientId, "-root")}
            className={className}
            isFocused={isFocused || isOpen}
            validationState={validationState || "rest"}
            isReadonly={isReadonly}
            focusMode={focusMode}
            ref={targetRef}
            themeMode={themeMode}
            appearance={appearance}
            onMouseDown={handleMouseDown}
            onBlur={(e: React.FocusEvent<HTMLDivElement>) => handleMultiSelectBasicFocus(e, "blur")}
            onFocus={(e: React.FocusEvent<HTMLDivElement>) => handleMultiSelectBasicFocus(e, "focus")}
            onClick={(e) => {
                e.stopPropagation();
                if (
                    isInteractive &&
                    !isOpen &&
                    document.activeElement !== localToggleRef.current &&
                    !localOpenState.current
                ) {
                    toggleMenu();
                }
                // Still focus even if we are disabled.
                localToggleRef.current?.focus();
            }}
            {...overrides?.rootProps}
        >
            <HiddenInput
                clientId={clientId}
                getHiddenInputValue={getHiddenInputValue}
                hiddenInputName={hiddenInputName}
                selectionType={selectedItems}
            />
            <MultiSelectValueContainer
                inputFieldHeight={inputFieldHeight}
                onChipNavigation={handleChipNavigation}
                activeIndex={activeIndex}
                maxSelectedRows={maxSelectedRows}
                numSelected={selectedItems.length}
                onHeightChange={setChipContentHeight}
                getSelectedItemProps={getSelectedItemProps}
                isReadonly={isReadonly}
                className={classes?.input}
                rollupSelectionAfter={rollupSelectionAfter}
                optionToString={optionToString}
                removeSelectedItem={removeSelectedItem}
                selectedItems={selectedItems}
                setActiveIndex={setActiveIndex}
                setSelectedItems={setSelectedItems}
                formatSelectedValue={formatSelectedValue}
                id={id}
                clientId={clientId}
                {...overrides?.valueContainerProps}
            >
                {selectedOptions.length == 0 && placeholder && (
                    <SelectPlaceholder inputFieldHeight={inputFieldHeight} hasContentBefore={false}>
                        {placeholder}
                    </SelectPlaceholder>
                )}
            </MultiSelectValueContainer>
            <Actions inputFieldHeight={inputFieldHeight} isReadonly={isReadonly} themeMode={themeMode}>
                <ToggleButton
                    className={SELECT_TOGGLE_ELEMENT_CLASS}
                    inputFieldHeight={inputFieldHeight}
                    clientId={clientId}
                    hideDropdownArrow={hideDropdownArrow}
                    isReadonly={isReadonly}
                    data-menu-test-id={menuId}
                    isHovered={isToggleHovered}
                    isActive={isToggleActive}
                    isOpen={isOpen}
                    onMouseEnter={onTAMouseEnter}
                    onMouseLeave={onTAMouseLeave}
                    onMouseDown={onTAMouseDown}
                    {...toggleButtonProps}
                    onClick={(e) => {
                        e.stopPropagation();
                        // we dispatch a click event with a distinct detail key if it's a label click
                        // this way we can functionally open the menu rather than
                        // relying on the blur and toggle click that causes the menu to close
                        // and quickly open again
                        if (e.detail === FORM_LABEL_CLICK_EVENT_ID) {
                            if (!isOpen && changeType.current !== useSelect.stateChangeTypes.MenuBlur) {
                                openMenu();
                            } else {
                                closeMenu();
                                changeType.current = null;
                            }
                        } else {
                            toggleButtonProps.onClick?.(e);
                        }
                    }}
                />
                {showClearAction && (
                    <SelectClearButton
                        inputFieldHeight={inputFieldHeight}
                        closeMenu={closeMenu}
                        setActiveIndex={setActiveIndex}
                        clearSelectedItems={() => setSelectedItems([])}
                        refocusTargetElement={() => {
                            localToggleRef.current?.focus();
                        }}
                        ref={clearButtonRef}
                        resetFiltering={() => {}}
                        isMultiSelect
                    />
                )}
            </Actions>
            <SelectMenu<Option>
                extraMenuProps={{
                    onKeyDown: (e) => {
                        providedOnKeyDown?.(e, {
                            highlightedIndex,
                            options: providedOptions,
                        });
                        switch (e.key) {
                            case "ArrowLeft": {
                                // Move focus from the toggle button to the last chip.
                                const lastChipIndex = Math.min(rollupSelectionAfter, selectedItems.length - 1);
                                setActiveIndex(lastChipIndex);
                                break;
                            }
                            case "Tab":
                                // Since the menu is portalled, we need to get focus back to our button.
                                e.preventDefault();
                                localToggleRef.current?.focus();
                                closeMenu();
                        }
                    },
                    "aria-multiselectable": isOpen ? "true" : undefined,
                    ...overrides?.menuProps,
                }}
                formatOption={formatOption || optionRenderer}
                isLoading={isLoading}
                menuWidth={menuWidth}
                className={classes?.menu}
                maxMenuHeight={maxMenuHeight}
                maxOptionsUntilVirtualization={maxOptionsUntilVirtualization}
                optionHeight={optionHeight}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                isOpen={isOpen}
                isOptionDisabled={isOptionDisabled}
                flyoutProps={{ ...flyoutProps, ...overrides?.selectFlyoutProps }}
                optionToString={optionToString}
                options={memoizedOptions}
                targetWidth={rootWidth}
                allSelected={allSelected}
                enableSelectAll={enableSelectAll}
                inputValue=""
                selectedItems={selectedItems}
                getOptionKey={getOptionKey}
            />
        </Root>
    );
};

let warnOnceForDeprecatedSetMenuIsOpenProp: undefined | (() => void);
